/**
 * components/chat/EncryptedPlaceholder.tsx — shimmering 🔒 "Encrypted message"
 * placeholder rendered INSIDE a chat bubble while an E2E message is waiting for
 * the shared key to resolve / a decrypt retry is in flight.
 *
 * Pure presentation: a soft opacity pulse (native-driver Animated loop) on a
 * lock glyph + label, tinted for my/their bubble. Never shows ciphertext.
 */
import React, { useEffect, useRef } from 'react';
import { Animated, Easing, StyleSheet, Text } from 'react-native';
import { Ionicons } from '../ui/Icon';
import { colors, fonts, spacing } from '../../constants/theme';

export default function EncryptedPlaceholder({
  isMine,
  label = 'Encrypted message',
}: {
  isMine: boolean;
  label?: string;
}) {
  const pulse = useRef(new Animated.Value(0.45)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 650,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.45,
          duration: 650,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  const tint = isMine ? 'rgba(255,255,255,0.85)' : colors.muted;

  return (
    <Animated.View
      style={[styles.row, { opacity: pulse }]}
      accessibilityLabel="Encrypted message — decrypting"
    >
      <Ionicons name="lock-closed" size={13} color={tint} />
      <Text style={[styles.label, { color: tint }]}>{label}</Text>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: 2,
  },
  label: {
    fontSize: 14,
    fontStyle: 'italic',
    ...fonts.regular,
  },
});
